import { styled } from "baseui";
import { Avatar } from "baseui/avatar";
import { Badge } from "baseui/badge";
import { Button, SHAPE, KIND, SIZE } from "baseui/button";
import { HeadingMedium } from "baseui/typography";
import { Select } from "baseui/select";
import { Input } from "baseui/input";
import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { defaultInstance, directApiInstance } from "../../api/httpClient";
import { type MRT_ColumnDef } from 'mantine-react-table';
import { Menu, Tooltip as MantineTooltip } from '@mantine/core';
import { IconDownload, IconPlus } from '@tabler/icons-react';
import { MantineDataTable } from "../../components/MantineDataTable";
import { Flex } from "../../components/Flex";
import { InvitationType } from "../../components/InvitationType";
import { formatDate } from "../../helpers/dateUtils";
import { formatPhoneForDisplay } from "../../helpers/phoneUtils";
import { useBusinessAccessInvitations, useExportBusinessAccessInvitations, useDoorPinAttentionInvitations, BusinessAccess } from "../../hooks/useBusinessAccessInvitations";
import { exportInvitationsToCSV } from "../../utils/csvExportUtils";
import { usePatchBusinessAccessId, usePatchInvitationId } from "../../api/generated";
import { cancelInvitation } from "../../api/invitationCancel";
import { NotificationService } from "../../services/notification";
import { useBusinesses } from "../../hooks/useBusinessAccessType";
import { useAccessTypeLabelMap, getAccessTypeLabel } from "../../hooks/useAccessTypeOptions";
import { useUserPermissions } from "../../hooks/useUserPermissions";
import { reAnalyzeFromS3 } from "../../services/documentAnalysis";

import { confirmButtonColor, confirmDialogLabels, type PassConfirmActionType } from './confirmDialogLabels';
import { InvitationProvider, useInvitationContext } from "./context/InvitationContext";
import { Modal, SIZE as MODAL_SIZE, ROLE } from "baseui/modal";
import { Drawer } from "baseui/drawer";
import { InvitationDetailView } from "./components/InvitationDetailView";
import { HikPinStatusChip } from "./components/HikPinPanel";
import { useHikEnabled } from "../../hooks/useHikEnabled";
import { toaster } from "baseui/toast";
import { FilterState } from "../../components/InlineFilters";
import { BaseDatePicker } from "../../components/BaseDatePicker";
import { DebouncedSearchInput } from "../../components/DebouncedSearchInput";
import { AlertTriangle, Building2, CalendarDays, Home, Filter, Search, X } from 'lucide-react';
import { Tooltip } from "baseui/tooltip";
import { S3Image } from "../../components/S3Image";
import { getChannelLabel, getDestinationParts, getDestinationText, isEventPass } from "../../utils/invitationDisplay";
import { useEvents } from "../../hooks/useEvents";
import { PassesPhone, type PhonePassCard } from "./PassesPhone";
import { awaitingPaymentActions } from "./awaitingPayment";
import { passClock } from "./passClock";
import type { PhoneOptionGroup } from "../../components/phone/PhoneListParts";
import { passTone } from "../BusinessPage/activity";
import { useT } from "../../i18n/admin";
import { toDayParam, combinedBookingService, CombinedPageError } from "../../api/businessAccessCombined";
import { BOOKING_HAS_PAYMENT_MESSAGE, getApiErrorMessage, isBookingHasPayment, isForbiddenError, isPassError } from '../../utils/apiErrors';
import { cancelBusinessAccessPass } from '../../api/passCancel';
import { isPassExpired } from './passExpiry';
import { tabWindow } from './passTabs';
import { passStatusLabel } from './passStatusLabel';
import { isSamePerson } from '../events/attendees';

/**
 * SAM-574: one date cell for the From / To / Created columns. `format` is the
 * i18n date formatter (month written out, tenant's zone); an absent or
 * unparseable value reads as the table's "--" instead of "Invalid Date".
 */
const DateCell = ({ value, format }: { value?: string | null; format: (v: string) => string }) => {
  const text = value ? format(value) : '';
  return text
    ? <span style={{ fontSize: '13px', color: '#374151', whiteSpace: 'nowrap' }}>{text}</span>
    : <span style={{ color: '#9CA3AF' }}>--</span>;
};

// Type assertion to handle BaseUI compatibility issues
const ModalComponent = Modal as any;
const DrawerComponent = Drawer as any;
const TooltipComponent = Tooltip as any;


const Header = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "24px 24px 0 24px",
  // On a phone the title stays on the left and "New pass" on the right, like Users and Activity.
  "@media screen and (max-width: 768px)": {
    padding: "16px 16px 0 16px",
    gap: "0.75rem",
  }
});

// The right-hand filters (dates, type, Export, door PIN): one row on a desktop, wrapping full width on a phone.
const ToolbarActions = styled('div', {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  // SAM-1312 (U4): wraps at every width, so on a narrow window the filters go to a second line instead of being cut.
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  gap: '10px',
  '@media screen and (max-width: 768px)': {
    marginLeft: 0,
    justifyContent: 'flex-start',
  },
});

const DateFilterBox = styled('div', {
  minWidth: '220px',
  '@media screen and (max-width: 768px)': {
    minWidth: 0,
    flex: '1 1 100%',
  },
});

const TabsRow = styled('div', {
  display: 'flex',
  borderBottom: '1px solid #E5E7EB',
  padding: '0 24px',
  marginTop: '8px',
  overflowX: 'auto',
  '@media screen and (max-width: 768px)': {
    padding: '0 16px',
  },
});


const ToolbarRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 24px',
  '@media screen and (max-width: 768px)': {
    flexDirection: 'column',
    padding: '12px 16px',
    alignItems: 'stretch',
  },
});

const SearchWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: '1',
  maxWidth: '280px',
  backgroundColor: '#EBEBEB',
  border: '1px solid #EBEBEB',
  borderRadius: '20px',
  padding: '7px 14px',
  '@media screen and (max-width: 768px)': {
    maxWidth: '100%',
  },
});

const SearchInputEl = styled('input', {
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '14px',
  color: '#374151',
  flex: 1,
  width: '100%',
  '::placeholder': {
    color: '#9CA3AF',
  },
});

const FiltersContainer = styled('div', {
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  padding: '20px',
  '@media screen and (max-width: 768px)': {
    padding: '12px',
  },
  margin: '0 24px 16px 24px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
});

const FiltersGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
});

const FilterActions = styled('div', {
  display: 'flex',
  gap: '8px',
  marginTop: '16px',
  justifyContent: 'flex-end',
});

const HeaderRight = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const Label = styled("span", {
  fontSize: "14px",
  fontWeight: "500",
  color: "#374151",
  whiteSpace: "nowrap",
});

// Main container - fills the TableContent wrapper exactly without causing body scroll
const Container = styled('div', {
  backgroundColor: '#fff',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const Main = styled("div", {
  flex: 1,
  padding: "0 24px 24px 24px",
  backgroundColor: "transparent",
  width: "100%",
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: 'hidden',
  boxSizing: 'border-box',
  '@media screen and (max-width: 768px)': {
    padding: '0 8px 16px 8px',
  },
});

const TableContainer = styled("div", {
  width: "100%",
  maxWidth: "100%",
  position: 'relative',
  overflow: "hidden",
  flex: 1,
  minHeight: 0,
});

// ── Guest Detail Fetch Hook ──────────────────────────────────────────────────
const useGuestUserDetail = (userId: number | undefined) => {
  return useQuery({
    queryKey: ['guest-user-detail', userId],
    queryFn: async () => {
      const response = await defaultInstance<{ data: any }>({
        method: 'GET',
        url: `/user/${userId}`,
      });
      return response.data?.data || response.data;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
};

// ── Single Guest Card ────────────────────────────────────────────────────────
// Track which guests have been analyzed (persists across remounts)
const analyzedGuestIds = new Set<number>();

export const GuestCard = ({
  guestBase,
  title,
}: {
    guestBase: { id?: number; name?: string; last_name?: string; tel?: string; email?: string; photo_url?: string | null;[key: string]: any };
    title: string;
}) => {
  const { fmt } = useT();
  const { data: enriched } = useGuestUserDetail(guestBase?.id);
  const [showLightbox, setShowLightbox] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [localUpdates, setLocalUpdates] = useState<Record<string, any>>({});

  // Auto re-analyze guest's document if missing key fields
  useEffect(() => {
    const guest = { ...guestBase, ...enriched };
    const guestId = guest?.id;
    const photoUrl = guest?.photo_url;
    if (
      guestId &&
      photoUrl &&
      !analyzedGuestIds.has(guestId) &&
      (!guest.idnum || !guest.birthdate || !guest.nationality)
    ) {
      analyzedGuestIds.add(guestId);
      (async () => {
        try {
          console.log(`[ReAnalyze Guest] Triggering for guest ${guest.id}...`);
          const result = await reAnalyzeFromS3(photoUrl);
          if (result?.data) {
            const updates: Record<string, any> = {};
            const d = result.data;
            if (!guest.idnum && d.number) updates.idnum = d.number;
            if (!guest.birthdate && d.DOB) updates.birthdate = d.DOB;
            if (!guest.nationality && d.nationality) updates.nationality = d.nationality;
            if (!guest.name && d.name) updates.name = d.name;
            if (!guest.last_name && d.LastName) updates.last_name = d.LastName;

            if (Object.keys(updates).length > 0) {
              console.log(`[ReAnalyze Guest] Updating user ${guest.id} with:`, updates);
              // Save to DB
              await defaultInstance({
                url: `/user/${guest.id}`,
                method: 'PATCH',
                data: updates,
              });
              // Update local display immediately — no need to refetch
              setLocalUpdates(updates);
            }
          }
        } catch (err) {
          console.error('[ReAnalyze Guest] Failed:', err);
        }
      })();
    }
  }, [guestBase, enriched]);

  // Merge local OCR-extracted fields into display values
  const merged = { ...guestBase, ...enriched, ...localUpdates };
  const fullName = `${merged?.name || ''} ${merged?.last_name || ''}`.trim() || '--';
  const nationality = merged?.nationality || '--';
  
  // BD column is 'birthdate' — same name as what frontend sends
  const rawDob = merged?.birthdate || merged?.date_of_birth;
  const dateOfBirth = rawDob
    ? fmt.date(rawDob)
    : '--';

  // idnum field from backend (saved as 'idnum'), fallback to id_number from /user/{id}
  const idNumber = merged?.idnum || merged?.id_number || '--';
  const idType = merged?.id_type || 'National ID';
  const photoUrl = merged?.photo_url || null;
  const contact = merged?.tel ? formatPhoneForDisplay(merged.tel) : merged?.email || '--';

  const fieldRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '14px 0',
    borderBottom: '1px solid #F3F4F6',
  };
  const labelStyle: React.CSSProperties = { fontSize: '14px', color: '#374151', fontWeight: 400 };
  const valueStyle: React.CSSProperties = { fontSize: '14px', color: '#111827', fontWeight: 400, textAlign: 'right', maxWidth: '55%' };
  const subValueStyle: React.CSSProperties = { fontSize: '12px', color: '#9CA3AF', textAlign: 'right' };

  return (
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: '0 0 16px 0' }}>{title}</h3>

      {/* Photo */}
      {photoUrl ? (
        <div
          onDoubleClick={() => setShowLightbox(true)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            marginBottom: '16px',
            borderRadius: '8px',
            overflow: 'hidden',
            aspectRatio: '1.586 / 1',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            cursor: 'zoom-in',
            position: 'relative',
          }}
        >
          <S3Image
            imageKey={photoUrl}
            alt={`${title} ID`}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', pointerEvents: 'none' }}
            showLoading={true}
          />
          {/* Hover overlay */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease',
            pointerEvents: 'none',
          }}>
            <span style={{
              color: '#fff',
              fontSize: '13px',
              fontWeight: 500,
              letterSpacing: '0.02em',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              Double click to view
            </span>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: '16px', borderRadius: '8px', backgroundColor: '#E5E7EB', height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#9CA3AF', fontSize: '14px' }}>No photo available</span>
        </div>
      )}

      {/* Lightbox Modal — rendered via portal above everything */}
      {showLightbox && photoUrl && ReactDOM.createPortal(
        <div
          onClick={() => setShowLightbox(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowLightbox(false); }}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(4px)',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => { (e.currentTarget).style.backgroundColor = 'rgba(255, 255, 255, 0.3)'; }}
            onMouseLeave={(e) => { (e.currentTarget).style.backgroundColor = 'rgba(255, 255, 255, 0.15)'; }}
          >
            ✕
          </button>

          {/* Full-size image */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '60vw', maxHeight: '70vh', cursor: 'default' }}
          >
            <S3Image
              imageKey={photoUrl}
              alt={`${title} ID - Full size`}
              style={{
                maxWidth: '60vw',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
              showLoading={true}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Fields */}
      <div>
        <div style={{ ...fieldRowStyle }}>
          <span style={labelStyle}>Full Name</span>
          <span style={valueStyle}>{fullName}</span>
        </div>

        <div style={{ ...fieldRowStyle }}>
          <span style={labelStyle}>ID</span>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              ...valueStyle,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '200px',
            }}>{idNumber}</div>
            <div style={subValueStyle}>{idType}</div>
          </div>
        </div>

        <div style={{ ...fieldRowStyle }}>
          <span style={labelStyle}>Nationality</span>
          <span style={valueStyle}>{nationality}</span>
        </div>

        <div style={{ ...fieldRowStyle }}>
          <span style={labelStyle}>Date of birth</span>
          <span style={valueStyle}>{dateOfBirth}</span>
        </div>

        <div style={{ ...fieldRowStyle, borderBottom: 'none' }}>
          <span style={labelStyle}>Contact</span>
          <div style={{ textAlign: 'right' }}>
            <div style={valueStyle}>{contact}</div>
            {guestBase?.email && guestBase?.tel && (
              <div style={subValueStyle}>{guestBase.email}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// SAM-1109: frames the guest whose row opened the pass.
const OpenedMark = ({ on, children }: { on: boolean; children: React.ReactNode }) =>
  on ? (
    <div style={{ border: '1px solid #0B5C4D', background: '#E7F1EE', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#0B5C4D', textAlign: 'right' }}>You opened this one</div>
      {children}
    </div>
  ) : (
    <>{children}</>
  );

// ── GuestsView ───────────────────────────────────────────────────────────────
export const GuestsView = ({
  invitationData,
  onShowId,
  highlight,
}: {
  invitationData: BusinessAccess | null;
  onShowId: (guest: { title: string; name: string; photo_url?: string | null }) => void;
  /** SAM-1109: the person whose row opened this pass (event attendees list); that guest is marked. */
  highlight?: { name: string; lastName: string; idnum?: string } | null;
}) => {
  if (!invitationData) {
    return (
      <Flex flexDirection="column" justifyContent="center" alignItems="center" height="200px">
        <p>No guest data available</p>
      </Flex>
    );
  }

  const mainGuest = invitationData.main_guest;
  // The list endpoints answer `business_guest`, the event attendees feed `business_guests`.
  const businessGuests = invitationData.business_guest || (invitationData as { business_guests?: typeof invitationData.business_guest }).business_guests || [];
  const totalCompanions = businessGuests.length;
  const opened = (g: Parameters<typeof isSamePerson>[0]) => !!highlight && isSamePerson(g, highlight);

  return (
    <div style={{ padding: '8px 0' }}>
      {/* Divider */}
      <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '0 0 24px 0' }} />

      {/* Main Guest */}
      {mainGuest && (
        <OpenedMark on={opened(mainGuest)}><GuestCard guestBase={mainGuest} title="Main Guest" /></OpenedMark>
      )}

      {/* Companions */}
      {totalCompanions > 0 && (
        <>
          <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '8px 0 24px 0' }} />
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: '0 0 20px 0' }}>
            Companions ({totalCompanions})
          </h3>
          {businessGuests.map((companion, index) => (
            <OpenedMark key={companion.id || index} on={opened(companion)}>
              <GuestCard guestBase={companion} title={`Companion ${index + 1}`} />
            </OpenedMark>
          ))}
        </>
      )}
    </div>
  );
};

/** SAM-944: the picker holds exactly today (the phone dropdown's «Today»). */
const isTodayRange = (range: Date[]): boolean =>
  range.length === 2 && toDayParam(range[0]) === toDayParam(new Date()) && toDayParam(range[1]) === toDayParam(new Date());

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const InvitationsContent = () => {
  const navigate = useNavigate();
  const { isAdmin, isBusinessAdmin, isPropertyManager, isInvitationManager: hasInvitationGroup, isResidentialInvitationManager, isAllInvitationManager, mainEstate } = useUserPermissions();
  // A business admin sees the passes of their own business, like the business invitation group.
  const isInvitationManager = hasInvitationGroup || isBusinessAdmin;
  // All-invitation managers see both entity types (no scoping). Single-role users are locked to their entity.
  const onlyBusinessInvitations = !isAdmin && !isAllInvitationManager && !isPropertyManager && isInvitationManager && !isResidentialInvitationManager;
  const onlyResidentialInvitations = !isAdmin && !isAllInvitationManager && !isPropertyManager && isResidentialInvitationManager && !isInvitationManager;
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [showQRDrawer, setShowQRDrawer] = useState(false);
  // Right-click quick actions on a row; the row itself opens the details drawer.
  const [rowMenu, setRowMenu] = useState<{ row: BusinessAccess; x: number; y: number } | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<BusinessAccess | null>(null);
  // SAM-1156: /invitations?pass=<id> opens that pass's detail (the Activity rows of a business link here). The
  // pass is read by id, wherever it falls in the date window the list shows.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepPassId = searchParams.get('pass');
  const { data: deepPassData } = useBusinessAccessInvitations(
    { id: deepPassId || undefined, entity: 'business_access', limit: 1 },
    { enabled: !!deepPassId },
  );
  const deepPass = deepPassId ? (deepPassData?.data || []).find((p) => String(p.id) === deepPassId) ?? null : null;
  const [searchTerm, setSearchTerm] = useState("");
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [activePageTab, setActivePageTab] = useState<'upcoming' | 'past'>('upcoming');
  // SAM-944: the phone's one dropdown can pick a status; it goes to the server by name, like the Past tab does.
  const [phoneStatus, setPhoneStatus] = useState<string | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string[]>([]);
  // SAM-307: "door PIN not OK" view — pending/failed PIN, stay in progress or check-in in the next 48 h.
  // SAM-1164: «Door PIN issues» (the filter and the chip) only exist for a tenant with Hik-Connect on.
  const hikEnabled = useHikEnabled();
  const [doorPinRequested, setDoorPinAttentionOnly] = useState(false);
  const doorPinAttentionOnly = hikEnabled && doorPinRequested;
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: PassConfirmActionType;
    invitation: BusinessAccess;
  } | null>(null);
  // SAM-1309 (U18): no default range in the picker; Upcoming and Past bring their own window (passTabs.ts).
  const [filters, setFilters] = useState<FilterState>({
    dateRange: [],
    status: [],
    destination: [],
    type: [],
  });
  // SAM-250 QA: the 90-day default must not count as an "active filter" — it's
  // just the initial view, not a user choice. Only flip this once the user
  // actually interacts with the date picker, so activeFiltersCount, the
  // "Clear Filters" button, and hasActiveFiltersOrSort (which controls
  // client- vs server-side pagination) stay accurate on a clean load.
  const [dateRangeTouched, setDateRangeTouched] = useState(false);
  // Client-side sorting state
  const [sortBy, setSortBy] = useState({
    column: 'edit_date',
    direction: 'DESC' as 'ASC' | 'DESC'
  });
  // SAM-574: the date columns render through the i18n formatter, so the month is
  // always written out in the active language and never dd/mm vs mm/dd.
  const { fmt, t } = useT();
  const queryClient = useQueryClient();
  const { updateInvitation } = useInvitationContext();
  
  // SAM-576: the window goes to the server. `/business_access/combined` takes
  // date_from / date_to (whole days, date_to inclusive) and matches any row whose
  // visit overlaps them, filtering and paginating in SQL — the range operators the
  // SAM-250 note said were missing landed with SAM-608. Filtering client-side could
  // only ever search the page on hand, so a date that matched on page 7 looked like
  // "no results", and the counter and page count came out of those rows.
  const [dateWindow, setDateWindow] = useState<{ date_from?: string; date_to?: string }>(() => tabWindow('upcoming'));
  useEffect(() => {
    const [from, to] = filters.dateRange;
    // Both ends: the user's range. Neither: the picker was cleared (no window at all) or never used (the tab's window).
    // One end only means a range half picked — keep the window that is applied rather than flashing the whole table
    // between the two clicks.
    if (from && to) setDateWindow({ date_from: toDayParam(from), date_to: toDayParam(to) });
    else if (!from && !to) setDateWindow(dateRangeTouched ? {} : tabWindow(activePageTab));
  }, [filters.dateRange, activePageTab, dateRangeTouched]);

  // SAM-575: the tab pins the status in its own parameter and the search box keeps `q`.
  // They used to share `q` — a free-text OR over guest, type, status and destination — so in
  // Past the tab always won and whatever the user typed never reached the server.
  const statusFilter = phoneStatus;
  const effectiveQ = searchTerm || undefined;

  // SAM-767: the invitation type is filtered by the server (/combined `entity`). Filtering it
  // in the browser only looked at the 20 rows of the current page, so business invitations
  // on other pages were unreachable and the type menu only listed what that page held.
  const scopedType = isPropertyManager && !isAdmin ? 'residential'
    : onlyBusinessInvitations ? 'business'
    : onlyResidentialInvitations ? 'residential'
    : filters.type.length === 1 ? filters.type[0] : undefined;
  const entity = scopedType === 'business' ? 'business_access' as const
    : scopedType === 'residential' ? 'invitation' as const
    : undefined;

  const {
    data,
    isLoading,
    refetch,
  } = useBusinessAccessInvitations({
    page,
    limit,
    // Property managers only see their estate's invitations
    ...(isPropertyManager && !isAdmin && mainEstate ? { estate_id: mainEstate } : {}),
    ...dateWindow,
    q: effectiveQ,
    entity,
    status: statusFilter,
  });

  // SAM-307: the door-PIN view has its own query (hik_pin_status=pending|failed over the
  // next 48 h or in progress, see useDoorPinAttentionInvitations); it replaces the page's rows while active.
  const doorPinQuery = useDoorPinAttentionInvitations(
    {
      ...(isPropertyManager && !isAdmin && mainEstate ? { estate_id: mainEstate } : {}),
      q: searchTerm || undefined,
    },
    { enabled: doorPinAttentionOnly },
  );

  // SAM-973: after a cancel (or any status change) every list over these rows is re-read, not only
  // the page on screen: the door-PIN view and the other cached windows would keep the old status.
  const refreshInvitations = () => {
    queryClient.invalidateQueries({ queryKey: ['business-access-invitations'] });
    refetch();
    if (doorPinAttentionOnly) doorPinQuery.refetch();
  };

  // Fetch all available businesses for destination filter
  const { data: allBusinessesData } = useBusinesses();

  // SAM-581: single source of truth for access-type labels, loaded from
  // the real business_access_type records (see useAccessTypeOptions.ts).
  const { map: accessTypeLabelMap } = useAccessTypeLabelMap();

  // Export hook - only fetches when triggered
  const { fetchAll: fetchExportRows } = useExportBusinessAccessInvitations({
    ...(isPropertyManager && !isAdmin && mainEstate ? { estate_id: mainEstate } : {}),
    ...dateWindow,
    q: effectiveQ,
    entity,
    status: statusFilter,
  });

  // Mutations for updating business access and invitation status
  const updateBusinessAccess = usePatchBusinessAccessId({
    mutation: {
      onSuccess: () => {
        NotificationService.success("Access status updated successfully");
        refreshInvitations();
      },
      onError: (error: any) => {
        console.error('Error updating business access status:', error);
        // A pass error (pass_expired, ...) is already explained by the generic toast (SAM-981).
        if (!isPassError(error)) NotificationService.error(isBookingHasPayment(error) ? BOOKING_HAS_PAYMENT_MESSAGE : "Error updating access status");
      },
    },
  });

  const updateInvitationStatus = usePatchInvitationId({
    mutation: {
      onSuccess: () => {
        NotificationService.success("Invitation status updated successfully");
        refreshInvitations();
      },
      onError: (error: any) => {
        console.error('Error updating invitation status:', error);
        // A pass error (pass_expired, ...) is already explained by the generic toast (SAM-981).
        if (!isPassError(error)) NotificationService.error("Error updating invitation status");
      },
    },
  });

  // Use data directly from API (search is handled by backend now)
  const searchFilteredData = doorPinAttentionOnly ? (doorPinQuery.data || []) : (data?.data || []);
  
  // Apply additional filters (date range filtering is now handled by API)
  const filteredData = React.useMemo(() => {
    if (!searchFilteredData) return [];
    
    return searchFilteredData.filter((item: BusinessAccess) => {
      // Determine entity type for this item
      let entityType = '';
      if (item.entity === 'business_access') {
        entityType = 'business';
      } else if (item.entity === 'invitation') {
        entityType = 'residential';
      } else if (typeof item.business_access_type_id === 'number') {
        if (item.business_access_type_id === 2) {
          entityType = 'business';
        } else {
          entityType = 'residential';
        }
      } else {
        entityType = 'residential';
      }

      // Property manager: force residential only
      if (isPropertyManager && !isAdmin && entityType !== 'residential') {
        return false;
      }

      // Invitation-only roles: restrict to their entity type
      if (onlyBusinessInvitations && entityType !== 'business') {
        return false;
      }
      if (onlyResidentialInvitations && entityType !== 'residential') {
        return false;
      }

      // Type filter - now entity-based
      if (filters.type.length > 0) {
        if (!filters.type.includes(entityType)) {
          return false;
        }
      }
      
      // Status filter
      if (filters.status.length > 0) {
        const statusName = item.status_name?.toLowerCase() || '';
        if (!filters.status.some(status => status.toLowerCase() === statusName)) {
          return false;
        }
      }
      
      // Destination filter - both types show estate names
      if (filters.destination.length > 0) {
        let destinationName = '';
        if (item.entity === 'business_access') {
          destinationName = item.business?.estate?.name || '';
        } else if (item.entity === 'invitation') {
          destinationName = item.estate?.name || '';
        } else {
          // Fallback for records without entity field
          destinationName = item.business?.estate?.name || item.estate?.name || '';
        }
        
        if (!filters.destination.includes(destinationName)) {
          return false;
        }
      }

      // SAM-576: the date window is a server filter now (date_from / date_to), so
      // there is nothing left to do here. It also means the range the picker shows
      // is the range that is applied, from the first load.

      return true;
    });
  }, [searchFilteredData, filters, isPropertyManager, isAdmin, onlyBusinessInvitations, onlyResidentialInvitations]);

  // Apply client-side sorting
  const sortedData = React.useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const sorted = [...filteredData].sort((a: BusinessAccess, b: BusinessAccess) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy.column) {
        case 'business_access_type_id':
          // Handle the new entity-based Type column
          aValue = a.entity || a.business_access_type_id;
          bValue = b.entity || b.business_access_type_id;
          break;
        case 'type_name':
          aValue = a.type_name || '';
          bValue = b.type_name || '';
          break;
        case 'main_guest_name':
          aValue = a.main_guest ? `${a.main_guest.name || ''} ${a.main_guest.last_name || ''}`.trim() : '';
          bValue = b.main_guest ? `${b.main_guest.name || ''} ${b.main_guest.last_name || ''}`.trim() : '';
          break;
        case 'business_name':
          aValue = a.business_name || '';
          bValue = b.business_name || '';
          break;
        case 'business_access_status_id':
          aValue = a.business_access_status_id;
          bValue = b.business_access_status_id;
          break;
        case 'start_date':
          aValue = new Date(a.start_date);
          bValue = new Date(b.start_date);
          break;
        case 'end_date':
          aValue = new Date(a.end_date);
          bValue = new Date(b.end_date);
          break;
        case 'create_date':
          aValue = new Date(a.create_date);
          bValue = new Date(b.create_date);
          break;
        case 'edit_date':
          aValue = new Date(a.edit_date || a.create_date);
          bValue = new Date(b.edit_date || b.create_date);
          break;
        default:
          return 0;
      }

      // Handle different data types
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortBy.direction === 'ASC' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortBy.direction === 'ASC' ? comparison : -comparison;
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortBy.direction === 'ASC' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });

    return sorted;
  }, [filteredData, sortBy]);
  
  // Create filter options from data - now entity-based
  // The types this user may see (not the types on the loaded page — see SAM-767 above).
  const typeOptions = React.useMemo(() => {
    if ((isPropertyManager && !isAdmin) || onlyResidentialInvitations) return [{ label: 'Residential', id: 'residential' }];
    if (onlyBusinessInvitations) return [{ label: 'Business', id: 'business' }];
    return [{ label: 'Business', id: 'business' }, { label: 'Residential', id: 'residential' }];
  }, [isPropertyManager, isAdmin, onlyBusinessInvitations, onlyResidentialInvitations]);

  const statusOptions = React.useMemo(() => {
    const uniqueStatuses = [...new Set((data?.data || []).map((item: BusinessAccess) => item.status_name).filter(Boolean))];
    return uniqueStatuses
      .sort((a, b) => (a as string).localeCompare(b as string, undefined, { numeric: true, sensitivity: 'base' }))
      .map(status => ({ label: passStatusLabel(status as string), id: status as string }));
  }, [data?.data]);
  
  const destinationOptions = React.useMemo(() => {

    // If admin, show all businesses from allBusinessesData
    if (isAdmin && allBusinessesData?.data) {
      const allDestinations = allBusinessesData.data
        .map((business: any) => business.estate?.name)
        .filter((name: string | undefined) => name !== undefined && name !== '')
        .sort((a: string, b: string) => a.localeCompare(b, undefined, {
          numeric: true,
          sensitivity: 'base',
          caseFirst: 'upper'
        }));

      // Remove duplicates
      const uniqueDestinations = Array.from(new Set(allDestinations));


      const options = uniqueDestinations.map((name: string) => ({
        label: name,
        id: name
      }));

      return options;
    }

    // For non-admin users, get destinations from current access records
    const destinationSet = new Set<string>();
    
    (data?.data || []).forEach((item: BusinessAccess) => {
      let destinationName = '';
      if (item.entity === 'business_access') {
        destinationName = item.business?.estate?.name || '';
      } else if (item.entity === 'invitation') {
        destinationName = item.estate?.name || '';
      } else {
        // Fallback for records without entity field
        destinationName = item.business?.estate?.name || item.estate?.name || '';
      }
      
      if (destinationName) {
        destinationSet.add(destinationName);
      }
    });
    
    const currentDestinations = Array.from(destinationSet)
      .sort((a, b) => a.localeCompare(b, undefined, { 
        numeric: true, 
        sensitivity: 'base',
        caseFirst: 'upper'
      }));
    
    
    // Create options for filtering
    const options = currentDestinations.map(name => ({
      label: name,
      id: name
    }));
    
    return options;
  }, [isAdmin, allBusinessesData?.data, data?.data]);
  
  // Calculate active filters count
  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    if (dateRangeTouched && filters.dateRange.length === 2) count++;
    if (filters.status.length > 0) count++;
    if (filters.destination.length > 0) count++;
    if (filters.type.length > 0) count++;
    return count;
  }, [filters, dateRangeTouched]);
  
  // Debounce local search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(localSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearchTerm]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Reset page to 1 when filters change.
  // NOTE (SAM-250 QA): date range is no longer sent to the server (see note
  // above the query hooks), so it no longer needs to trigger a refetch here —
  // it's applied client-side in `filteredData`.
  useEffect(() => {
    setPage(1);
  }, [filters]);

  // Reset page to 1 when limit changes  
  useEffect(() => {
    setPage(1);
  }, [limit]);


  
  // SAM-578: the search box is NOT a client-side filter. `effectiveQ` goes to
  // `/business_access/combined`, which filters and paginates server-side, so the
  // rows on hand are page N of the matches — not the matches. Paginating over
  // them again hid every match past the first page and reported the page size as
  // the total: a search matching 704 rows read "1-20 of 20". Everything below is
  // applied client-side over the loaded page and still has to paginate here.
  // The type, the user's own scope and a property manager's estate are server-side too (SAM-767).
  const hasClientSideFilterOrSort = doorPinAttentionOnly
    || filters.status.length > 0
    || filters.destination.length > 0
    || sortBy.column !== 'edit_date'
    || sortBy.direction !== 'DESC';

  // Whether anything at all narrows the list — used for the "Clear Filters" button.
  const hasActiveFiltersOrSort = hasClientSideFilterOrSort || !!searchTerm;

  const displayData = hasClientSideFilterOrSort
    ? (sortedData || []).slice((page - 1) * limit, page * limit)
    : (data?.data || []); // Server-paginated already, with or without a search.

  // SAM-1320 (U35): the pass of an event says «Event» and names the event. The names are only fetched when the
  // page holds such a pass (the event list is not every role's to read).
  const { data: eventList } = useEvents({ enabled: displayData.some(isEventPass) });
  const eventNameOf = React.useCallback((row: BusinessAccess): string | undefined => {
    if (!isEventPass(row)) return undefined;
    const ev = (eventList || []).find((e) => String(e.id) === String(row.event_id));
    return ev ? (ev.name_en || ev.name_es || ev.name || undefined) : undefined;
  }, [eventList]);

  const totalPages = hasClientSideFilterOrSort
    ? Math.max(1, Math.ceil((sortedData?.length || 0) / limit))
    : data?.total_pages || 1;

  const totalRecords = hasClientSideFilterOrSort
    ? sortedData?.length || 0
    : data?.total_elements || 0;

  // SAM-944 (canvas PhonePasses): on a phone each pass is a card, and ONE dropdown holds range and status.
  const phoneFilter = activePageTab === 'past' ? 'past'
    : phoneStatus ? `status:${phoneStatus}`
      : dateRangeTouched && isTodayRange(filters.dateRange) ? 'today' : 'upcoming';
  const [seenStatuses, setSeenStatuses] = useState<string[]>([]);
  useEffect(() => {
    // The statuses seen so far: once one is chosen the list only holds that status, the others must stay pickable.
    const names = statusOptions.map((o) => o.id).filter((n) => n.toLowerCase() !== 'checked out');
    setSeenStatuses((prev) => {
      const next = [...new Set([...prev, ...names])].sort((x, y) => x.localeCompare(y));
      return next.length === prev.length ? prev : next;
    });
  }, [statusOptions]);
  const phoneGroups: PhoneOptionGroup[] = [
    { options: [
      { id: 'upcoming', label: t('phonePasses.filter.upcoming') },
      { id: 'today', label: t('phonePasses.filter.today') },
      { id: 'past', label: t('phonePasses.filter.past') },
    ] },
    { label: t('phonePasses.filter.statusGroup'), options: [...new Set([...seenStatuses, ...(phoneStatus ? [phoneStatus] : [])])].map((n) => ({ id: `status:${n}`, label: passStatusLabel(n) })) },
  ].filter((g) => g.options.length > 0);
  const pickPhoneFilter = (id: string) => {
    setPhoneStatus(id.startsWith('status:') ? id.slice(7) : undefined);
    setActivePageTab(id === 'past' ? 'past' : 'upcoming');
    if (id === 'today') {
      const now = new Date();
      setFilters((prev) => ({ ...prev, dateRange: [new Date(now.getFullYear(), now.getMonth(), now.getDate()), new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)] }));
      setDateRangeTouched(true);
    } else {
      setFilters((prev) => ({ ...prev, dateRange: [] }));
      setDateRangeTouched(false);
    }
    setPage(1);
  };
  const phonePassCards: PhonePassCard[] = displayData.map((row) => {
    const guestName = `${row.main_guest?.name || ''} ${row.main_guest?.last_name || ''}`.trim() || '--';
    const isBusiness = row.entity === 'business_access' || row.business_access_type_id === 2;
    const destination = getDestinationText(row, eventNameOf(row));
    const start = row.start_date ? new Date(row.start_date) : null;
    const today = !!start && start.toDateString() === new Date().toDateString();
    const from = row.start_date ? fmt.date(row.start_date) : '';
    const to = row.end_date && row.end_date !== row.start_date ? fmt.date(row.end_date) : '';
    const day = to ? `${from} – ${to}` : from || '--';
    const when = today ? t('phonePasses.todayWhen', { when: passClock(row.start_date, fmt.time) ?? t('phonePasses.allDay') }) : day;
    const code = row.booking?.confirmation_code || '';
    const service = row.booking ? combinedBookingService(row.booking) : '';
    return {
      key: `${row.entity ?? ''}:${row.id}`,
      when,
      today,
      name: guestName,
      sub: (code
        ? [t('phonePasses.bookingCode', { code }), service]
        : [isEventPass(row) ? t('booking.access.event') : isBusiness ? t('phonePasses.business') : t('phonePasses.guest'), destination]).filter(Boolean).join(' · '),
      status: row.status_name ? { label: passStatusLabel(row.status_name), tone: passTone(Number(row.business_access_status_id ?? 0)) } : null,
      open: () => handleRowDoubleClick(row),
    };
  });

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when limit changes
    // The query will automatically refetch due to the dependency on limit in the queryKey
  };

  // Handle double-click on table row
  const handleRowDoubleClick = React.useCallback((rowData: BusinessAccess) => {
    setSelectedInvitation(rowData);
    setShowQRDrawer(true);
  }, []);

  // The deep-linked pass opens once it has loaded.
  React.useEffect(() => {
    if (deepPass) {
      setSelectedInvitation(deepPass);
      setShowQRDrawer(true);
    }
  }, [deepPass?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to get actions based on status
  const getActionsForStatus = (statusId: number, row?: BusinessAccess) => {
    const actions = [];

    // SAM-981: an ended residential pass takes no change (cancel, check-in, ...), so only the details remain.
    const isBusinessRow = !!row && (row.entity === 'business_access' || (typeof row.business_access_type_id === 'number' && !row.entity));
    if (row && isPassExpired({ ...row, entity: isBusinessRow ? 'business_access' : 'invitation' })) return [{ label: "View details" }];
    
    // Add status-specific actions based on the new status IDs
    switch (statusId) {
      case 1: // Pending
        actions.push({ label: "Confirmed" });
        actions.push({ label: "Check In" });
        actions.push({ label: "Cancel" });
        break;
        
      case 2: // Confirmed
        actions.push({ label: "Check In" });
        actions.push({ label: "Cancel" });
        break;
        
      case 3: // Verified
        actions.push({ label: "Check In" });
        actions.push({ label: "Cancel" });
        break;
        
      case 4: // Checked In
        actions.push({ label: "Check Out" });
        actions.push({ label: "Cancel" });
        break;
        
      case 5: // Checked Out
        // Note: For end date check, we would need the full row data
        // For now, always show both options - the backend will validate
        actions.push({ label: "Check In" });
        actions.push({ label: "Cancel" });
        break;
        
      default:
        // For unknown status or statuses like Expired (7), Cancel (6)
        // Don't add any status-changing actions. A pass waiting for its payment link (Processing)
        // can only be cancelled (SAM-1293).
        awaitingPaymentActions(row?.status_name).forEach((label) => actions.push({ label }));
        break;
    }
    
    // Always show View details as the last option
    actions.push({ label: "View details" });
    
    return actions;
  };

  useEffect(() => {
    if (selectedInvitation) {
    
      
      const invitationData = {
        legacy_code: selectedInvitation.legacy_code || '',
        code: selectedInvitation.id,
        business_name: selectedInvitation.business_name || '',
        guests: {
          mainGuest: {
            firstName: selectedInvitation.main_guest?.name || '',
            lastName: selectedInvitation.main_guest?.last_name || '',
            phoneNumber: selectedInvitation.main_guest?.tel || '',
          }
        },
        accessType: [{
          id: selectedInvitation.business_access_type_id,
          label: getAccessTypeLabel(accessTypeLabelMap, selectedInvitation.business_access_type_id)
        }],
        duration: {
          date: selectedInvitation.start_date
        },
        gate_rules: selectedInvitation.gate_rules
      };
      
     
      updateInvitation(invitationData);
    }
  }, [selectedInvitation, updateInvitation, accessTypeLabelMap]);


  // Helper to get status style
  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return { backgroundColor: "rgba(255, 183, 77, 0.15)", color: "#B45309", borderColor: "rgba(255, 183, 77, 0.3)" };
      case "verified":
        return { backgroundColor: "rgba(59, 130, 246, 0.15)", color: "#1D4ED8", borderColor: "rgba(59, 130, 246, 0.3)" };
      case "cancel":
        return { backgroundColor: "rgba(239, 68, 68, 0.15)", color: "#DC2626", borderColor: "rgba(239, 68, 68, 0.3)" };
      case "expired":
        return { backgroundColor: "rgba(156, 163, 175, 0.15)", color: "#6B7280", borderColor: "rgba(156, 163, 175, 0.3)" };
      case "processing":
      case "proccesing":
        return { backgroundColor: "rgba(251, 191, 36, 0.15)", color: "#B45309", borderColor: "rgba(251, 191, 36, 0.3)" };
      case "confirmed":
        return { backgroundColor: "rgba(34, 197, 94, 0.15)", color: "#15803D", borderColor: "rgba(34, 197, 94, 0.3)" };
      case "checked in":
        return { backgroundColor: "rgba(20, 184, 166, 0.15)", color: "#0F766E", borderColor: "rgba(20, 184, 166, 0.3)" };
      case "checked out":
        return { backgroundColor: "rgba(168, 85, 247, 0.15)", color: "#7C3AED", borderColor: "rgba(168, 85, 247, 0.3)" };
      default:
        return { backgroundColor: "rgba(156, 163, 175, 0.15)", color: "#6B7280", borderColor: "rgba(156, 163, 175, 0.3)" };
    }
  };

  const columns = useMemo<MRT_ColumnDef<BusinessAccess>[]>(
    () => [
      {
        accessorKey: 'entity',
        header: 'Access type',
        size: 120,
        enableHiding: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const value = row.original.entity || row.original.business_access_type_id;
          let icon = null;
          let label = '';

          if (isEventPass(row.original)) {
            icon = <CalendarDays size={14} />;
            label = 'Event';
          } else if (value === 'business_access' || value === 2) {
            icon = <Building2 size={14} />;
            label = 'Business';
          } else {
            icon = <Home size={14} />;
            label = 'Residential';
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#374151' }}>
              <span style={{ color: '#6B7280', display: 'flex', alignItems: 'center' }}>{icon}</span>
              {label}
            </div>
          );
        },
      },
      {
        accessorKey: 'main_guest.name',
        header: 'Guest Name',
        size: 170,
        enableHiding: false,
        Cell: ({ row }) => {
          const mainGuest = row.original.main_guest;
          // `main_guest` can arrive as an empty object (a pass with no user row),
          // which used to render an empty cell instead of the table's "--".
          const fullName = `${mainGuest?.name || ''} ${mainGuest?.last_name || ''}`.trim() || '--';
          const phone = mainGuest?.tel ? formatPhoneForDisplay(mainGuest.tel) : '';

          return fullName !== '--' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span
                style={{
                  fontSize: '14px',
                  color: '#111827',
                  fontWeight: 500,
                  textDecoration: 'underline',
                  textDecorationColor: '#9CA3AF',
                  textUnderlineOffset: '2px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                onClick={(e) => { e.stopPropagation(); handleRowDoubleClick(row.original); }}
              >
                {fullName}
              </span>
              {phone && (
                <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{phone}</span>
              )}
              {/* Phones hide the Destination column; it rides under the guest instead. */}
              {isMobile && getDestinationText(row.original, eventNameOf(row.original)) && (
                <span style={{ fontSize: '12px', color: '#6B7280' }}>{getDestinationText(row.original, eventNameOf(row.original))}</span>
              )}
            </div>
          ) : <span style={{ color: '#9CA3AF' }}>--</span>;
        },
      },
      {
        accessorKey: 'business_name',
        id: 'destination',
        header: 'Destination',
        size: 150,
        Cell: ({ row }) => {
          const { primary, secondary } = getDestinationParts(row.original, eventNameOf(row.original));
          if (!primary && !secondary) return <span style={{ color: '#9CA3AF' }}>--</span>;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
              <span style={{ fontSize: '14px', color: '#374151' }}>{primary || '--'}</span>
              {secondary && (
                <span style={{ fontSize: '12px', color: '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {secondary}
                </span>
              )}
            </div>
          );
        },
      },
      {
        // Invitations carry no delivery channel; the backend answers their
        // origin (resident app / office / imported). Business passes answer
        // their business_access_channel name.
        accessorKey: 'channel',
        id: 'channel',
        header: 'Channel',
        size: 110,
        enableSorting: false,
        Cell: ({ row }) => {
          const label = getChannelLabel(row.original);
          return (
            <span style={{ fontSize: '13px', color: label ? '#374151' : '#9CA3AF' }}>
              {label || '--'}
            </span>
          );
        },
      },
      // SAM-574: when the visit happens and when it was created. The three come
      // from the same response the CSV export already writes (start_date,
      // end_date, create_date); they are hideable because the table is wide.
      {
        accessorKey: 'start_date',
        id: 'start_date',
        header: 'From',
        size: 120,
        Cell: ({ row }) => <DateCell value={row.original.start_date} format={fmt.date} />,
      },
      {
        accessorKey: 'end_date',
        id: 'end_date',
        header: 'To',
        size: 120,
        Cell: ({ row }) => <DateCell value={row.original.end_date} format={fmt.date} />,
      },
      {
        accessorKey: 'create_date',
        id: 'create_date',
        header: 'Created',
        size: 120,
        Cell: ({ row }) => <DateCell value={row.original.create_date} format={fmt.date} />,
      },
      {
        accessorKey: 'status_name',
        header: 'Status',
        size: 110,
        enableHiding: false,
        Cell: ({ row }) => {
          const value = row.original.status_name || '--';
          const statusStyle = getStatusStyle(value);

          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
            <span style={{
              ...statusStyle,
              fontSize: '12px',
              fontWeight: 500,
              borderRadius: '20px',
              padding: '3px 10px',
              border: `1px solid ${statusStyle.borderColor}`,
              textTransform: 'capitalize',
              whiteSpace: 'nowrap',
              display: 'inline-block',
            }}>
              {passStatusLabel(value)}
            </span>
            {/* SAM-307: door PIN pending / failed; nothing for ok, skipped or absent. */}
            {hikEnabled && <HikPinStatusChip row={row.original} />}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobile, fmt.date, hikEnabled, eventNameOf]
  );

  const handleExport = async () => {
    try {
      setIsExporting(true);

      setExportProgress(null);
      const exportRows = await fetchExportRows((loaded, total) => setExportProgress({ loaded, total }));

      if (exportRows.length === 0) {
        toaster.warning('No data available to export', {
          autoHideDuration: 3000,
        });
        return;
      }

      let filteredExportData = exportRows;

      // Role-based scope: invitation-only users can export only their entity type
      if (onlyBusinessInvitations || onlyResidentialInvitations) {
        filteredExportData = filteredExportData.filter((item: BusinessAccess) => {
          let entityType = '';
          if (item.entity === 'business_access') {
            entityType = 'business';
          } else if (item.entity === 'invitation') {
            entityType = 'residential';
          } else if (typeof item.business_access_type_id === 'number' && item.business_access_type_id === 2) {
            entityType = 'business';
          } else {
            entityType = 'residential';
          }
          if (onlyBusinessInvitations) return entityType === 'business';
          if (onlyResidentialInvitations) return entityType === 'residential';
          return true;
        });
      }

      if (filters.type.length > 0) {
        filteredExportData = filteredExportData.filter((item: BusinessAccess) => {
          let entityType = '';
          if (item.entity === 'business_access') {
            entityType = 'business';
          } else if (item.entity === 'invitation') {
            entityType = 'residential';
          } else if (typeof item.business_access_type_id === 'number' && item.business_access_type_id === 2) {
            entityType = 'business';
          } else {
            entityType = 'residential';
          }
          return filters.type.includes(entityType);
        });
      }

      if (filters.status.length > 0) {
        filteredExportData = filteredExportData.filter((item: BusinessAccess) => {
          const statusName = item.status_name?.toLowerCase() || '';
          return filters.status.some(status => status.toLowerCase() === statusName);
        });
      }

      if (filters.destination.length > 0) {
        filteredExportData = filteredExportData.filter((item: BusinessAccess) => {
          let destinationName = '';
          if (item.entity === 'business_access') {
            destinationName = item.business?.estate?.name || '';
          } else if (item.entity === 'invitation') {
            destinationName = item.estate?.name || '';
          } else {
            destinationName = item.business?.estate?.name || item.estate?.name || '';
          }
          return filters.destination.includes(destinationName);
        });
      }

      // SAM-576: the export query carries the same date window as the list.

      exportInvitationsToCSV(filteredExportData);

      toaster.positive(`Successfully exported ${filteredExportData.length} records`, {
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('Export error:', error);
      // SAM-849: say how far it got; the file is not downloaded half-empty.
      toaster.negative(
        error instanceof CombinedPageError
          ? `Export failed on page ${error.page} of ${error.totalPages}. Nothing was downloaded. Please try again.`
          : 'Error exporting data. Please try again.',
        { autoHideDuration: 6000 },
      );
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const handleInvitationAction = React.useCallback((row: BusinessAccess, action: string) => {
    switch (action) {
      case "Confirmed":
      case "Check In":
      case "Check Out":
      case "Cancel":
        // Show confirmation dialog for status change actions
        setConfirmAction({
          type: action as 'Confirmed' | 'Check In' | 'Check Out' | 'Cancel',
          invitation: row
        });
        setShowConfirmModal(true);
        break;
      case "View details":
        // Handle view details logic - show the QR modal
        setSelectedInvitation(row);
        setShowQRDrawer(true);
        break;
      default:
        break;
    }
  }, []);

  // Fire-and-forget push so the host knows their guest moved through the
  // gate. The push copy lives in `sam_core.notification_template` — we just
  // send the template code + variables, the backend renders the final text.
  // JWT carries the tenant; errors only log so they never block the UI.
  const notifyHostOfStatusChange = (invitation: any, statusId: number, isBusinessAccess: boolean) => {
    const hostUserId: number | undefined = isBusinessAccess
      ? invitation?.main_guest_user_id
      : invitation?.host_user_id;
    if (!hostUserId) return;

    const guestName: string = (() => {
      const g = invitation?.main_guest;
      const full = [g?.name, g?.last_name].filter(Boolean).join(' ').trim();
      return full || 'Tu invitado';
    })();

    let templateCode: string;
    let type: string;
    let fallbackTitle: string;
    let fallbackBody: string;
    if (statusId === 4) {
      templateCode = 'guest_checkin';
      type = 'guest_checkin';
      fallbackTitle = `${guestName} checked in`;
      fallbackBody = `${guestName} just checked in. Get ready to receive them.`;
    } else if (statusId === 5) {
      templateCode = 'guest_checkout';
      type = 'guest_checkout';
      fallbackTitle = `${guestName} checked out`;
      fallbackBody = `${guestName} has left.`;
    } else {
      return;
    }

    const linkType = isBusinessAccess ? 'access' : 'invitation';
    directApiInstance({
      url: '/notifications/send',
      method: 'POST',
      data: {
        template_code: templateCode,
        variables: { guest_name: guestName },
        // Sent as last-resort fallback in case the template row is missing.
        title: fallbackTitle,
        description: fallbackBody,
        type,
        user_ids: [hostUserId],
        send_push: true,
        action_link_object: { type: linkType, id: String(invitation.id) },
      },
    }).catch((err: any) => {
      console.warn('[Invitations] push notification failed:', err?.response?.data || err?.message || err);
    });
  };

  const handleConfirmAction = () => {
    if (!confirmAction) return;

    const { type, invitation } = confirmAction;

    // Determine which mutation to use based on entity type
    const isBusinessAccess = invitation.entity === 'business_access' ||
                           (typeof invitation.business_access_type_id === 'number' && !invitation.entity);

    switch (type) {
      case "Confirmed":
        // Pending (1) → Confirmed (2)
        if (isBusinessAccess) {
          updateBusinessAccess.mutate({
            id: invitation.id,
            data: { business_access_status_id: 2 }
          });
        } else {
          updateInvitationStatus.mutate({
            id: invitation.id,
            data: { status_id: 2 }
          });
        }
        break;
      case "Check In":
        // Pending (1), Confirmed (2), or Verified (3) → Checked In (4)
        if (isBusinessAccess) {
          updateBusinessAccess.mutate(
            { id: invitation.id, data: { business_access_status_id: 4 } },
            { onSuccess: () => notifyHostOfStatusChange(invitation, 4, true) },
          );
        } else {
          updateInvitationStatus.mutate(
            { id: invitation.id, data: { status_id: 4 } },
            { onSuccess: () => notifyHostOfStatusChange(invitation, 4, false) },
          );
        }
        break;
      case "Check Out":
        // Checked In (4) → Checked Out (5)
        if (isBusinessAccess) {
          updateBusinessAccess.mutate(
            { id: invitation.id, data: { business_access_status_id: 5 } },
            { onSuccess: () => notifyHostOfStatusChange(invitation, 5, true) },
          );
        } else {
          updateInvitationStatus.mutate(
            { id: invitation.id, data: { status_id: 5 } },
            { onSuccess: () => notifyHostOfStatusChange(invitation, 5, false) },
          );
        }
        break;
      case "Cancel":
        // Any status → Cancel (6)
        if (isBusinessAccess) {
          // SAM-814: PUT /business_access/{id} voids a paid pass's charge and cancels it in one call.
          // The selected business's key, as every other call: the server (#829) lets a tenant admin act on the passes
          // of the whole tenant, so the browser no longer carries other businesses' keys.
          cancelBusinessAccessPass(invitation.id)
            .then(() => {
              NotificationService.success("Access status updated successfully");
            })
            .catch((error: any) => {
              console.error('Error cancelling the pass:', error?.response?.status, error?.response?.data ?? error);
              // A 403 can come from the gateway (key) as well as from the lambda: say what the server answered.
              const serverText = error?.response?.data?.error || error?.response?.data?.message;
              NotificationService.error(
                isForbiddenError(error)
                  ? `${t('passPayment.forbidden')} (${serverText || 'HTTP 403'})`
                  : getApiErrorMessage(error, 'Error updating access status'),
              );
            })
            .finally(() => {
              refreshInvitations();
              queryClient.invalidateQueries({ queryKey: ['business-payments'] });
            });
        } else {
          // S-38: invitations are cancelled by PUT /invitation/{id}, not by PATCH /crud/invitation.
          cancelInvitation(invitation.id)
            .then((outcome) => {
              if (outcome === 'cancelled') NotificationService.success("Invitation cancelled");
              else NotificationService.info("This invitation was already cancelled");
              refreshInvitations();
            })
            .catch((error: any) => {
              // 403 and the rest: httpClient already showed the server's message.
              console.error('Error cancelling invitation:', error);
            });
        }
        break;
    }

    // Close confirmation modal and drawer after successful action
    setShowConfirmModal(false);
    setConfirmAction(null);
    setShowQRDrawer(false);
    setSelectedInvitation(null);
  };

  const handleCancelAction = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    // Don't close the drawer on cancel - let user continue viewing the details
  };


  const handleCloseQRDrawer = () => {
    setShowQRDrawer(false);
    setSelectedInvitation(null);
    if (deepPassId) {
      const next = new URLSearchParams(searchParams);
      next.delete('pass');
      setSearchParams(next, { replace: true });
    }
  };

  // Handle column sorting
  const handleColumnSort = (columnTitle: string) => {
    
    let newOrderBy = '';
    let newOrderDirection: 'ASC' | 'DESC' = 'ASC';
    
    // Map column titles to database field names
    switch (columnTitle) {
      case 'Type':
        newOrderBy = 'business_access_type_id';
        break;
      case 'Guest Name':
        newOrderBy = 'main_guest_name'; // Assuming this field exists in API
        break;

      case 'Destination':
        // Both business_access and invitation records show estate names
        newOrderBy = 'estate_name';
        break;
      case 'Status':
        newOrderBy = 'business_access_status_id';
        break;
      case 'From':
        newOrderBy = 'start_date';
        break;
      case 'To':
        newOrderBy = 'end_date';
        break;
      case 'Last Modified':
        newOrderBy = 'edit_date';
        break;
      default:
        return; // Don't sort unsupported columns
    }
    
    // Toggle direction if clicking the same column, otherwise default to ASC
    if (sortBy.column === newOrderBy) {
      newOrderDirection = sortBy.direction === 'ASC' ? 'DESC' : 'ASC';
    } else {
      newOrderDirection = newOrderBy === 'edit_date' ? 'DESC' : 'ASC'; // Default Last Modified to DESC
    }
    
    setSortBy({
      column: newOrderBy,
      direction: newOrderDirection
    });
    
    // Reset to first page when sorting changes
    setPage(1);
  };

  return (
    <Container>
      {isMobile ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <PassesPhone
            items={phonePassCards}
            loading={doorPinAttentionOnly ? doorPinQuery.isLoading : isLoading}
            page={page}
            limit={limit}
            total={totalRecords}
            onPage={setPage}
            query={localSearchTerm}
            onQuery={setLocalSearchTerm}
            filter={phoneFilter}
            groups={phoneGroups}
            onFilter={pickPhoneFilter}
            onNew={() => navigate('/invitations/new')}
          />
        </div>
      ) : (
        <>
      <Header>
        <HeadingMedium style={{ margin: 0 }}>Passes</HeadingMedium>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* SAM-199 — the Access flow is now a full page (/invitations/new) instead of a
              drawer opened in place; see AccessPage.tsx. */}
          <Button
            size={SIZE.compact}
            onClick={() => navigate('/invitations/new')}
            startEnhancer={() => <IconPlus size={16} />}
            overrides={{
              BaseButton: {
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: 'var(--tenant-primary, #111827)',
                  ':hover': {
                    backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)',
                  },
                }
              }
            }}
          >
            New pass
          </Button>
        </div>
      </Header>

      {/* Upcoming / Past tabs */}
      <TabsRow>
        {(['upcoming', 'past'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActivePageTab(tab);
              setPage(1);
              setLocalSearchTerm('');
              setSearchTerm('');
            }}
            style={{
              padding: '10px 4px',
              marginRight: '24px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activePageTab === tab ? 600 : 400,
              color: activePageTab === tab ? 'var(--tenant-primary, #111827)' : '#6B7280',
              borderBottom: activePageTab === tab ? '2px solid var(--tenant-primary, #111827)' : '2px solid transparent',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
              marginBottom: '-1px',
              textTransform: 'capitalize',
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </TabsRow>

      {/* Toolbar: Search + Type filter + Export */}
      <ToolbarRow>
        <SearchWrapper>
          <Search size={16} color="#111827" />
          <SearchInputEl
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            placeholder="Search..."
          />
          {localSearchTerm && (
            <button
              onClick={() => { setLocalSearchTerm(''); setSearchTerm(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#9CA3AF' }}
            >
              <X size={14} />
            </button>
          )}
        </SearchWrapper>

        <ToolbarActions>
          {/* SAM-576: the range is applied server-side (date_from / date_to), including
              the default range, so what the picker shows is what the list contains. */}
          <DateFilterBox>
            <BaseDatePicker
              mode="range"
              rangeValue={{
                from: filters.dateRange[0] || undefined,
                to: filters.dateRange[1] || undefined,
              }}
              onRangeChange={(range) => {
                // Keep a half-picked range so the calendar can show the first click;
                // `dateWindow` only moves once both ends are in (SAM-576).
                const newDateRange = range?.from
                  ? (range?.to ? [range.from, range.to] : [range.from])
                  : [];
                setFilters(prev => ({ ...prev, dateRange: newDateRange }));
                setDateRangeTouched(true);
              }}
              placeholder="Select dates"
            />
          </DateFilterBox>

          {/* Invitation type - native select for clean Figma look */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <select
              value={selectedTypeFilter[0] || ''}
              onChange={(e) => {
                const val = e.target.value;
                const vals = val ? [val] : [];
                setSelectedTypeFilter(vals);
                setFilters(prev => ({ ...prev, type: vals }));
                setPage(1);
              }}
              style={{
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                minWidth: '160px',
                height: '36px',
                padding: '0 32px 0 12px',
                fontSize: '14px',
                color: selectedTypeFilter.length ? '#111827' : '#6B7280',
                backgroundColor: '#EBEBEB',
                border: '1px solid #EBEBEB',
                borderRadius: '8px',
                cursor: 'pointer',
                outline: 'none',
                lineHeight: '1',
              }}
            >
              <option value="">Pass type</option>
              {typeOptions.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
            <svg
              style={{ position: 'absolute', right: '10px', pointerEvents: 'none', color: '#6B7280' }}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>

          {/* SAM-307: door PIN not OK, stay in progress or check-in in the next 48 h (SAM-1164: Hik tenants only) */}
          {hikEnabled && (
          <button
            type="button"
            onClick={() => { setDoorPinAttentionOnly((v) => !v); setPage(1); }}
            aria-pressed={doorPinAttentionOnly}
            title="Invitations whose door PIN is pending or failed, with check-in in the next 48 hours or a stay in progress"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 14px',
              fontSize: '14px',
              fontWeight: 500,
              color: doorPinAttentionOnly ? 'var(--bad-fg)' : '#374151',
              backgroundColor: doorPinAttentionOnly ? 'var(--bad-bg)' : '#EBEBEB',
              border: doorPinAttentionOnly ? '1px solid color-mix(in srgb, var(--danger) 35%, white)' : '1px solid #EBEBEB',
              borderRadius: '8px',
              cursor: 'pointer',
              outline: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <AlertTriangle size={14} />
            Door PIN issues
          </button>
          )}

          {/* Export CSV - native button for clean Figma look */}
          <button
            onClick={handleExport}
            disabled={isExporting || !data?.data || data.data.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 14px',
              fontSize: '14px',
              fontWeight: 500,
              color: isExporting || !data?.data || data.data.length === 0 ? '#9CA3AF' : '#374151',
              backgroundColor: '#EBEBEB',
              border: '1px solid #EBEBEB',
              borderRadius: '8px',
              cursor: isExporting || !data?.data || data.data.length === 0 ? 'not-allowed' : 'pointer',
              outline: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <IconDownload size={14} />
            {isExporting
              ? (exportProgress && exportProgress.total > 0
                ? `Exporting… ${exportProgress.loaded.toLocaleString('en-US')}/${exportProgress.total.toLocaleString('en-US')}`
                : 'Exporting…')
              : 'Export CSV'}
          </button>
        </ToolbarActions>
      </ToolbarRow>

      {showFilters && (
        <FiltersContainer>
          <FiltersGrid>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 500, color: '#6B7280', marginBottom: '8px', display: 'block' }}>Status</label>
              <Select
                options={statusOptions}
                value={statusOptions.filter(opt => filters.status.includes(opt.id))}
                onChange={(params) => setFilters(prev => ({ ...prev, status: params.value.map((v: any) => v.id) }))}
                placeholder="All statuses"
                multi
                clearable
              />
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 500, color: '#6B7280', marginBottom: '8px', display: 'block' }}>Destination</label>
              <Select
                options={destinationOptions}
                value={destinationOptions.filter(opt => filters.destination.includes(opt.id))}
                onChange={(params) => setFilters(prev => ({ ...prev, destination: params.value.map((v: any) => v.id) }))}
                placeholder="All destinations"
                multi
                clearable
              />
            </div>
          </FiltersGrid>
          {(filters.status.length > 0 || filters.destination.length > 0) && (
            <FilterActions>
              <Button
                size={SIZE.compact}
                kind={KIND.tertiary}
                onClick={() => { setFilters({ dateRange: [], status: [], destination: [], type: [] }); setSelectedTypeFilter([]); setDateRangeTouched(false); }}
                startEnhancer={() => <X size={16} />}
              >
                Clear Filters
              </Button>
            </FilterActions>
          )}
        </FiltersContainer>
      )}

      <Main>
        <TableContainer>
          <MantineDataTable
            columns={columns}
            data={displayData}
            loading={doorPinAttentionOnly ? doorPinQuery.isLoading : isLoading}
            manualPagination
            pageCount={totalPages}
            totalRecords={totalRecords}
            state={{
              pagination: {
                pageIndex: page - 1,
                pageSize: limit,
              },
            }}
            onPaginationChange={(updater: any) => {
              const newState = typeof updater === 'function'
                ? updater({ pageIndex: page - 1, pageSize: limit })
                : updater;
              if (newState.pageSize !== limit) {
                setLimit(newState.pageSize);
                if (page !== 1) setPage(1);
              } else if (newState.pageIndex !== page - 1) {
                setPage(newState.pageIndex + 1);
              }
            }}
            onRowClick={(row) => handleRowDoubleClick(row)}
            onRowContextMenu={(row, e) => setRowMenu({ row, x: e.clientX, y: e.clientY })}
            enableGlobalFilter={false}
            initialState={{
              columnVisibility: isMobile
                // Phones keep guest + status; the dates ride in the detail drawer.
                ? { entity: false, channel: false, destination: false, start_date: false, end_date: false, create_date: false }
                // SAM-1323 (U4): Created is off by default so the table fits at 1440 with the menu open; the column menu brings it back
                : { create_date: false },
            }}
          />
        </TableContainer>
      </Main>
        </>
      )}

      <Menu
        opened={!!rowMenu}
        onChange={(opened) => { if (!opened) setRowMenu(null); }}
        withinPortal
        position="bottom-start"
        shadow="sm"
        zIndex={1000}
      >
        <Menu.Target>
          <div
            aria-hidden
            style={{ position: 'fixed', left: rowMenu?.x ?? 0, top: rowMenu?.y ?? 0, width: 1, height: 1, pointerEvents: 'none' }}
          />
        </Menu.Target>
        <Menu.Dropdown>
          {rowMenu && getActionsForStatus(rowMenu.row.business_access_status_id || rowMenu.row.status_id || 1, rowMenu.row).map((action) => (
            <Menu.Item
              key={action.label}
              color={action.label === 'Cancel' ? 'red' : undefined}
              onClick={() => {
                const target = rowMenu.row;
                setRowMenu(null);
                handleInvitationAction(target, action.label);
              }}
            >
              {action.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      {showQRDrawer && (
        <DrawerComponent
          isOpen={true}
          onClose={handleCloseQRDrawer}
          anchor="right"
          overrides={{
            Root: {
              style: {
                zIndex: 10000, // Ensure drawer is above Mantine table elements
              }
            },
            Backdrop: {
              style: {
                zIndex: 10000, // Ensure backdrop is also above Mantine table elements
              }
            },
            DrawerContainer: {
              style: {
                // Full screen on phones; a side panel from tablet up.
                width: isMobile ? "100vw" : "40vw",
                maxWidth: isMobile ? "100vw" : "500px",
                minWidth: isMobile ? "0" : "400px",
                zIndex: 10001, // Container should be above backdrop
              },
            },
            DrawerBody: {
              style: {
                padding: "0",
                // BaseUI's default body margins pushed the pinned action bar off a phone screen.
                marginTop: "0",
                marginBottom: "0",
                marginLeft: "0",
                marginRight: "0",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              },
            },
          }}
        >
          {selectedInvitation && (
            <InvitationDetailView
              row={selectedInvitation}
              eventName={eventNameOf(selectedInvitation)}
              isMobile={isMobile}
              actions={getActionsForStatus(selectedInvitation.business_access_status_id || selectedInvitation.status_id || 1, selectedInvitation)
                .map((a) => a.label)
                .filter((label) => label !== 'View details')}
              onAction={(action) => {
                // Keep the drawer open and show the confirmation over it
                setConfirmAction({
                  type: action as 'Confirmed' | 'Check In' | 'Check Out' | 'Cancel',
                  invitation: selectedInvitation,
                });
                setShowConfirmModal(true);
              }}
              renderGuestDetail={(guest, title) => <GuestCard guestBase={guest} title={title} />}
            />
          )}
        </DrawerComponent>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && confirmAction && (
        <ModalComponent
          isOpen={true}
          onClose={handleCancelAction}
          size={SIZE.default}
          role={ROLE.dialog}
          overrides={{
            Root: {
              style: {
                zIndex: 999999,
              },
            },
            Backdrop: {
              style: {
                zIndex: 999999,
              },
            },
            Dialog: {
              style: {
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                borderBottomLeftRadius: "16px",
                borderBottomRightRadius: "16px",
                padding: "24px",
                width: "400px",
                maxWidth: "90vw",
                zIndex: 999999,
              },
            },
            DialogContainer: {
              style: {
                zIndex: 999999,
              },
            },
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                {confirmDialogLabels(confirmAction.type, t).title}
              </h3>
              <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                {confirmAction.type === 'Confirmed' && t('invitation.confirm.confirmBody')}
                {confirmAction.type === 'Check In' && t('invitation.confirm.checkInBody')}
                {confirmAction.type === 'Check Out' && t('invitation.confirm.checkOutBody')}
                {confirmAction.type === 'Cancel' && t('invitation.confirm.cancelBody')}
              </p>
              
              {/* Guest Information */}
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('invitation.confirm.guest')} {confirmAction.invitation.main_guest?.name} {confirmAction.invitation.main_guest?.last_name}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {t('invitation.confirm.destination')} {confirmAction.invitation.business_name}
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                size="compact"
                kind="secondary"
                onClick={handleCancelAction}
                overrides={{
                  Root: {
                    style: { minWidth: '80px' }
                  }
                }}
              >
                {confirmAction.type === 'Cancel' ? t('invitation.confirm.back') : 'Cancel'}
              </Button>
              <Button
                size="compact"
                onClick={handleConfirmAction}
                overrides={{
                  Root: {
                    style: {
                      backgroundColor: confirmButtonColor(confirmAction.type),
                      color: '#fff',
                      minWidth: '80px'
                    }
                  }
                }}
              >
                {confirmDialogLabels(confirmAction.type, t).button}
              </Button>
            </div>
          </div>
        </ModalComponent>
      )}

    </Container>
  );
};

export const Invitations = () => {
  return (
    <InvitationProvider>
      <InvitationsContent />
    </InvitationProvider>
  );
};
